import { useCallback, useEffect, useState } from "react";
import { Briefcase, FileSpreadsheet, FileText, HandCoins, PiggyBank, Receipt, Users } from "lucide-react";
import { api } from "@/lib/api";
import { usePoll } from "@/lib/live";
import { useSettings } from "@/context/SettingsContext";
import { exportSections, logoDataUrl } from "@/lib/exporter";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDate, fmtDateTime, fmtTime, LOAN_STATUS, rupiah, todayStr } from "@/lib/format";

const MODAL_LABEL = { kasbon: "Alokasi Modal", setor_balik: "Setor Kembali", pencairan: "Pencairan", angsuran: "Angsuran" };
const MODAL_SIGN = { kasbon: 1, setor_balik: -1, pencairan: -1, angsuran: 1 };
const time = (s) => fmtTime(s);
const byResort = (rows) => [...rows].sort((a, b) => (a.resort_name || "~").localeCompare(b.resort_name || "~"));

const buildSections = (d) => [
  { key: "pendanaan", icon: Briefcase, title: "Riwayat Pendanaan", head: ["Jam", "Resort", "Jenis", "Keterangan", "Nominal"], money: [4],
    rows: byResort(d.modal).map((m) => [time(m.created_at), m.resort_name || "Umum", MODAL_LABEL[m.type] || m.type, m.note || "", (MODAL_SIGN[m.type] || 1) * m.amount]),
    total: d.modal.reduce((s, m) => s + (MODAL_SIGN[m.type] || 1) * m.amount, 0) },
  { key: "anggota", icon: Users, title: "Riwayat Anggota Baru", head: ["Jam", "Resort", "NIK", "Nama"], money: [],
    rows: byResort(d.members).map((m) => [time(m.created_at), m.resort_name || "-", m.nik, m.name]) },
  { key: "pinjaman", icon: HandCoins, title: "Riwayat Pinjaman", head: ["Jam", "Resort", "No. Pinjaman", "Nama", "Status", "Pokok"], money: [5],
    rows: byResort(d.loans).map((l) => [time(l.disbursed_at || l.created_at), l.resort_name || "-", l.loan_no || "-", l.member_name, LOAN_STATUS[l.status]?.label || l.status, l.principal]),
    total: d.loans.reduce((s, l) => s + l.principal, 0) },
  { key: "angsuran", icon: Receipt, title: "Riwayat Angsuran", head: ["Jam", "Resort", "NIK", "Nama", "Nominal"], money: [4],
    rows: byResort(d.angsuran).map((t) => [time(t.created_at), t.resort_name || "-", t.nik, t.member_name, t.amount]),
    total: d.angsuran.reduce((s, t) => s + t.amount, 0) },
  { key: "simpanan", icon: PiggyBank, title: "Riwayat Simpanan", head: ["Jam", "Resort", "NIK", "Nama", "Jenis", "Nominal"], money: [5],
    rows: byResort(d.simpanan).map((t) => [time(t.created_at), t.resort_name || "-", t.nik, t.member_name, t.type === "tarik" ? "Tarik" : "Setor", t.type === "tarik" ? -t.amount : t.amount]),
    total: d.simpanan.reduce((s, t) => s + (t.type === "tarik" ? -t.amount : t.amount), 0) },
];

const Section = ({ s }) => (
  <div className="panel !p-0 overflow-hidden" data-testid={`today-${s.key}`}>
    <div className="flex items-center gap-3 p-4">
      <span className="h-9 w-9 rounded-xl bg-primary/12 text-primary grid place-items-center shrink-0"><s.icon className="h-4 w-4" /></span>
      <div className="flex-1 min-w-0"><p className="font-heading font-semibold">{s.title}</p><p className="text-xs text-muted-foreground">{s.rows.length} data</p></div>
      {s.total !== undefined && <p className={`font-mono font-bold ${s.total < 0 ? "text-rose-500" : "text-primary"}`} data-testid={`today-${s.key}-total`}>{rupiah(s.total)}</p>}
    </div>
    {s.rows.length === 0 ? <p className="text-sm text-muted-foreground pb-5 text-center">Belum ada data hari ini.</p> : (
      <div className="overflow-x-auto border-t">
        <Table>
          <TableHeader><TableRow>{s.head.map((h, i) => <TableHead key={h} className={`${i === 0 ? "pl-4" : ""} ${s.money.includes(i) ? "text-right pr-4" : ""}`}>{h}</TableHead>)}</TableRow></TableHeader>
          <TableBody>{s.rows.map((r, ri) => (
            <TableRow key={ri}>{r.map((c, i) => (
              <TableCell key={i} className={`text-sm whitespace-nowrap ${i === 0 ? "pl-4 font-mono" : ""} ${s.money.includes(i) ? `text-right pr-4 font-mono ${c < 0 ? "text-rose-500" : ""}` : ""}`}>{s.money.includes(i) ? rupiah(c) : c}</TableCell>
            ))}</TableRow>
          ))}</TableBody>
        </Table>
      </div>
    )}
  </div>
);

export default function LaporanHarianPage() {
  const [d, setD] = useState(null);
  const { settings, logoSrc } = useSettings();
  const load = useCallback(() => { api.get("/petugas/today").then((r) => setD(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  usePoll(load);
  if (!d) return <p className="text-muted-foreground">Memuat...</p>;
  const sections = buildSections(d);
  const exp = async (kind) => exportSections(kind, {
    logo: kind === "pdf" ? await logoDataUrl(logoSrc) : null,
    title: `${settings.app_name} — Laporan Hari Ini`, subtitle: fmtDate(todayStr()), filename: `laporan-hari-ini-${todayStr()}`,
    sections: sections.map((s) => ({ title: `${s.title}${s.total !== undefined ? ` (Total ${rupiah(s.total)})` : ""}`, head: s.head, rows: s.rows, money: s.money })),
  });
  return (
    <div className="max-w-5xl space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="font-heading text-2xl font-bold">Laporan Hari Ini</h1><p className="text-sm text-muted-foreground">{fmtDate(todayStr())}</p></div>
        <div className="flex gap-2">
          <Button variant="outline" className="h-10 rounded-full" onClick={() => exp("pdf")} data-testid="today-export-pdf"><FileText className="h-4 w-4 mr-1.5" />PDF</Button>
          <Button variant="outline" className="h-10 rounded-full" onClick={() => exp("excel")} data-testid="today-export-excel"><FileSpreadsheet className="h-4 w-4 mr-1.5" />Excel</Button>
        </div>
      </div>
      {sections.map((s) => <Section key={s.key} s={s} />)}
      <p className="text-[11px] text-muted-foreground">Diperbarui {fmtDateTime(new Date().toISOString())}</p>
    </div>
  );
}
